import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { PopupCustomComponentGeneric } from 'src/app/popup-custom/popup-custom-content.component';
import { ErrorApiModel } from 'src/app/shared/models/api/error/error.apimodel';
import { InteractionService } from 'src/app/shared/services/interaction.service';
import { BonLivraisonTemperatureStatus } from '../../models/bon-livraison-temperature-status.model';
import { BonLivraisonService, PlatTemperatureStatus, ReleveRelivraison } from '../../services/bon-livraison.service';
import { RelivraisonPopupValidationModel } from './relivraison-popup-validation.model';
import { LivraisonSignatureComponent } from '../../livraison-bon-livraison/livraison-signature/livraison-signature.component';
import { BonLivraisonPlatRelivraisonModel } from '../../models/bon-livraison-plat-relivraison.model';
import { BonLivraisonPrestationModel } from '../../models/bon-livraison-prestation.model';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-relivraison-popup-validation',
  templateUrl: './relivraison-popup-validation.component.html',
  styleUrls: ['./relivraison-popup-validation.component.scss']
})
export class RelivraisonPopupValidationComponent extends PopupCustomComponentGeneric<RelivraisonPopupValidationModel> implements OnInit, OnDestroy {

  @ViewChild('form', { static: true })
  public form: NgForm; // FormGroup

  @ViewChild(LivraisonSignatureComponent, { static: false })
  public signatureControl: LivraisonSignatureComponent;

  public isLoading: boolean = false;
  public erreurValidationMessage: string;

  //#region data property override
  private _data: any;
  public get data(): any {
    return this._data;
  }
  public set data(data: any) {
    this._data = data;
    this.loadModel();
  }
  //#endregion

  relivraisonPrestations: BonLivraisonPrestationModel[] = [];

  private _relevesLivraison: ReleveRelivraison;
  public get nonConformiteTemperaturePlats(): PlatTemperatureStatus[] { return this._relevesLivraison && this._relevesLivraison.nonConformiteTemperaturePlats; }

  public commentaire: string;
  public relivraisonDate: Date = new Date();

  // declare enum property to allow use in component template. see: http://stackoverflow.com/a/40034109/590741
  public TemperatureStatusEnum: typeof BonLivraisonTemperatureStatus = BonLivraisonTemperatureStatus; // tslint:disable-line: variable-name

  public get hasNonConformiteTemperature(): boolean {
    return this.nonConformiteTemperaturePlats && this.nonConformiteTemperaturePlats.length > 0;
  }

  public get hasPlatFaults(): boolean {
    // Manque ou Excédent
    return this.hasNonConformiteTemperature;
  }

  public get allowRelivraison(): boolean {
    if (!this.model.bonLivraison.peutMettreEnConformite) {
      return false;
    }
    return this.model.bonLivraison.relivraisonSignature == null;
  }

  public get isCommentaireRequired() {
    // le commentaire est requis seulement en cas de non-conformite
    return this.hasPlatFaults;
  }

  public showNonConformePrompt: boolean = false;

  constructor(
    private _bonLivraisonService: BonLivraisonService,
    private _router: Router,
    private _interactionService: InteractionService
  ) {
    super();
  }

  ngOnInit() {
  }

  ngOnDestroy(): void {
  }

  private loadModel() {
    this.commentaire = this.model.bonLivraison.relivraisonCommentaire;

    this.relivraisonPrestations = this.computeRelivraisonPrestations();
    this._relevesLivraison = this._bonLivraisonService.getReleveRelivraison(this.relivraisonPrestations, this.model.bonLivraison.settings);
  }

  private computeRelivraisonPrestations(): BonLivraisonPrestationModel[] {
    // on ne garde que les prestations qui ont des composants relivrés (quantiteLivreeRelative < 0)
    return this.model.bonLivraison.bonLivraisonPrestations
      .map(p => {
        const fpList = p.bonLivraisonFamillePlats
          .map(fp => {
            const pList = fp.bonLivraisonPlats
              .map(pl => {
                const relivraisonComposants = pl.bonLivraisonComposants
                  .filter(c => c.quantiteLivreeRelative < 0);
                if (relivraisonComposants.length > 0) {
                  return {
                    ...pl,
                    bonLivraisonComposants: relivraisonComposants
                  };
                }
                return null;
              })
              .filter(pl => pl != null);
            if (pList.length > 0) {
              return {
                ...fp,
                bonLivraisonPlats: pList
              };
            }
            return null;
          })
          .filter(fp => fp != null);
        if (fpList.length > 0) {
          return {
            ...p,
            bonLivraisonFamillePlats: fpList
          };
        }
        return null;
      })
      .filter(p => p != null);
  }

  onTemperatureChanged() {
    this._relevesLivraison = this._bonLivraisonService.getReleveRelivraison(this.relivraisonPrestations, this.model.bonLivraison.settings);
  }

  DeclarerLivraison(declareConforme: boolean) {
    this.erreurValidationMessage = '';

    if (!this.validateForm()) {
      if (!this.checkSignature()) {
        this.erreurValidationMessage = 'Signature requise';
      }
      return;
    }

    this.isLoading = true;

    this.relivrerBon(declareConforme)
      .pipe(
        tap(bonLivraison => {
          this.isLoading = false;

          //update the bonLivraison in the model with the new data from the server
          this.model.bonLivraison = bonLivraison;
          this.model.result = declareConforme ? 'DeclarerConformite' : 'DeclarerNonConformite';

          this._interactionService.showSimpleMessage('Déclaration de relivraison', `Votre déclaration de relivraison a été prise en compte.`);

          this.hide();
        }),
        catchError((error: ErrorApiModel) => {
          this.erreurValidationMessage = error.Message;
          return [];
        }),
        finalize(() => {
          this.isLoading = false;
          this.hide();
        })
      )
      .subscribe();
  }

  private relivrerBon(declareConforme: boolean) {

    const releves: BonLivraisonPlatRelivraisonModel[]
      = Array.from(new Set(this._relevesLivraison.temperaturePlats.map(n => n.plat)))
        .map(p => ({
          bonLivraisonPlatId: p.bonLivraisonPlatId,
          relivraisonTemperature: p.relivraisonTemperature
        }));

    const signatureImage = this.signatureControl.toDataURL();
    return this._bonLivraisonService.declarerBonRelivre(this.model.bonLivraison.code, releves, this.commentaire, signatureImage, declareConforme);
  }

  hide() {
    this.emitter.next();
  }

  private validateForm(): boolean {
    this.validateAllFormFields(this.form.form);
    return this.form.valid
      && this.checkSignature();
  }

  private validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  scrollToElement(targetElement: HTMLElement) {
    targetElement.scrollIntoView();
  }

  private checkSignature(): boolean {
    return (!!this.model.bonLivraison.relivraisonSignature) || (this.signatureControl && !this.signatureControl.isEmpty());
  }
}
